import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  Renderer2,
  NgZone,
} from '@angular/core';
import { AuthService } from 'src/app/services/auth.service';
import { Helper } from 'src/app/helper/defult';
import html2pdf from 'html2pdf.js';
import { MatDialog } from '@angular/material/dialog';
import { PopupConfirmationComponent } from 'src/app/container/elements/popup-confirmation/popup-confirmation.component';
import { Router } from '@angular/router';
import { HttpIOService } from 'src/app/services/http-io.service';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { DynamicSnacbarErrorComponent } from 'src/app/container/elements/dynamic-snacbar-error/dynamic-snacbar-error.component';

@Component({
  selector: 'app-cv-preview',
  templateUrl: './cv-preview.component.html',
  styleUrls: ['./cv-preview.component.scss'],
})
export class CvPreviewComponent implements OnInit {
  userDetails: any;
  isLoading: boolean = false;
  isDownloading: boolean = false;
  geminiSuggestions: any;
  geminiRefinedCV: any;

  // PDF options
  options = {
    margin: [0.5, 0.2, 0.5, 0.2],
    filename: 'My_CV.pdf',
    image: { type: 'jpeg', quality: 1.0 },
    html2canvas: {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
    },
    jsPDF: {
      unit: 'in',
      format: 'a4',
      orientation: 'portrait',
    },
  };

  @ViewChild('cvToDownload', { static: false }) cvToDownload!: ElementRef;

  constructor(
    private auth: AuthService,
    private helper: Helper,
    private renderer: Renderer2,
    private dialog: MatDialog,
    private ngZone: NgZone,
    private router: Router,
    private io: HttpIOService,
    private snackBar: MatSnackBar
  ) {
    let state = history.state;
    if (!state.profileRecords) {
      this.snackBar
        .openFromComponent(DynamicSnacbarErrorComponent, {
          data: 'Please create or update your profile before previewing your CV.',
          verticalPosition: 'top', // 'top' | 'bottom'
          horizontalPosition: 'right', //'start' | 'center' | 'end' | 'left' | 'right'
          duration: 6000,
        })
        .afterOpened()
        .subscribe(this.helper.setOnTop);

      this.router.navigate(['/jobseeker-profile']);

      return;
    }

    const profile = state.profileRecords || {};
    const educations = state.educationAllRecords || [];
    const experience = state.experianceAllRecords || [];
    const certifications = state.licenseAllRecords || [];
    const skills = state.skillAllRecords || [];
    const languages = profile.languages || [];

    this.userDetails = {
      firstName: profile.firstName ?? '',
      lastName: profile.lastName ?? '',
      designation: profile.designation ?? '',
      contactDetails: {
        number: profile.contactNumber ?? '',
        email: profile.email ?? '',
        address: profile.userLocation ?? '',
        linkedIn: profile.linkedInId ?? '',
      },
      about: this.stripHtmlTags(profile.about) ?? '',
      educations: Array.isArray(educations)
        ? educations.map((e: any) => ({
            courseName: e?.courseName ?? '',
            formattedDate: e?.endYear ?? '',
            universityName: e?.universityName ?? '',
          }))
        : [],
      skills: Array.isArray(skills) ? skills : [],
      certifications: Array.isArray(certifications)
        ? certifications.map((c: any) => ({
            name: c?.name ?? '',
            date: c?.issueDate ?? '',
            authority: c?.issuingOrganization ?? '',
          }))
        : [],
      languages: Array.isArray(languages) ? languages : [],
      experience: Array.isArray(experience)
        ? experience.map((exp: any) => ({
            Title: exp?.title ?? '',
            startDate: exp?.startDate ?? '',
            endDate: exp?.endDate ?? 'Present',
            descriptionPoints:
              typeof exp?.description === 'string'
                ? exp.description
                    .split('\n')
                    .filter((point: string) => point.trim() !== '')
                : [],
          }))
        : [],
    };
  }

  ngOnInit(): void {}

  /**
   * Strips HTML tags from a given string.
   * @param htmlString The string potentially containing HTML tags.
   * @returns The string with all HTML tags removed.
   */
  stripHtmlTags(htmlString: string): string {
    const doc = new DOMParser().parseFromString(htmlString, 'text/html');
    return doc.body.textContent || '';
  }

  /**
   * Downloads the CV as a PDF.
   * It handles both mobile and desktop views by adjusting styles for accurate PDF capture.
   * @param modal The message to display in the success confirmation popup. Defaults to 'cv download started'.
   */
  async downloadCv(modal: string = 'cv download started') {
    const container = this.cvToDownload?.nativeElement;
    if (!container) {
      console.error('CV container element (#cvToDownload) not found.');
      return;
    }

    const cvTemplateElement = container.children[0] as HTMLElement;
    if (!cvTemplateElement) {
      console.error(
        'CV template element to download was not found inside the container.'
      );
      return;
    }

    // Check if we are in the mobile view using the same breakpoint as your SCSS
    const isMobile = window.innerWidth <= 768;

    if (isMobile) {
      // --- MOBILE-ONLY LOGIC ---
      // This logic will only run on smaller screens

      const originalTransform = cvTemplateElement.style.transform;
      // Your mobile CSS applies `position: absolute` so we need to handle it
      const originalPosition = cvTemplateElement.style.position;

      // Temporarily remove scaling and position constraints for an accurate PDF capture
      this.renderer.setStyle(cvTemplateElement, 'transform', 'none');
      this.renderer.setStyle(cvTemplateElement, 'position', 'static');
      this.renderer.setStyle(container, 'height', 'auto');

      try {
        this.isDownloading = true;

        await (html2pdf as any)()
          .from(cvTemplateElement)
          .set(this.options)
          .save();
        const successData = modal;
        this.ngZone.run(() => {
          this.dialog.closeAll();
          const dialogOpen = this.dialog.open(PopupConfirmationComponent, {
            height: 'auto',
            width: '450px',
            data: { data: successData },
          });

          dialogOpen.afterClosed().subscribe(() => {
            this.ngZone.run(() => {
              // this.router.navigate(['/job-search']);
              localStorage.removeItem('redirectTo');
            });
          });
        });
      } catch (error) {
        console.error('Error generating PDF on mobile:', error);
      } finally {
        // ALWAYS restore the original styles to put the mobile UI back to normal
        this.renderer.setStyle(
          cvTemplateElement,
          'transform',
          originalTransform
        );
        this.renderer.setStyle(
          cvTemplateElement,
          'position',
          originalPosition || 'absolute'
        );
        this.renderer.setStyle(container, 'height', null); // Resets the inline style
        this.isDownloading = false;
      }
    } else {
      // --- DESKTOP-ONLY LOGIC ---
      try {
        this.isDownloading = true;

        await (html2pdf as any)()
          .from(cvTemplateElement)
          .set(this.options)
          .save();
        const successData = modal;
        this.ngZone.run(() => {
          this.dialog.closeAll();
          const dialogOpen = this.dialog.open(PopupConfirmationComponent, {
            height: 'auto',
            width: '450px',
            data: { data: successData },
          });

          dialogOpen.afterClosed().subscribe(() => {
            this.ngZone.run(() => {
              // this.router.navigate(['/job-search']);
              localStorage.removeItem('redirectTo');
            });
          });
        });
      } catch (error) {
        console.error('Error generating PDF on desktop:', error);
      } finally {
        this.isDownloading = false;
      }
    }
  }

  /**
   * Initiates the CV review process.
   * Generates a PDF blob of the CV and sends it for review.
   * On success, navigates to the review landing page with the review results.
   */
  async requestCvReview() {
    const container = this.cvToDownload?.nativeElement;
    if (!container) {
      console.error('CV container element (#cvToDownload) not found.');
      return;
    }

    const cvTemplateElement = container.children[0] as HTMLElement;
    if (!cvTemplateElement) {
      console.error(
        'CV template element to download was not found inside the container.'
      );
      return;
    }

    try {
      this.isLoading = true;
      const pdfBlob = await (html2pdf as any)()
        .from(cvTemplateElement)
        .set(this.options)
        .output('blob');

      await this.uploadAndRequestReview(pdfBlob);

      // navigate to the review landing page with the review results
      this.router.navigate(['/cv-review'], {
        state: {
          showATS: true,
          geminiSuggestions: this.geminiSuggestions,
          geminiRefinedCV: this.geminiRefinedCV,
        },
      });
    } catch (error) {
      console.error('Error generating PDF for review:', error);
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Uploads the generated PDF blob and requests a CV review from the backend.
   * Sets `geminiSuggestions` and `geminiRefinedCV` upon successful review.
   * @param pdfBlob The PDF blob of the CV to be uploaded and reviewed.
   */
  private async uploadAndRequestReview(pdfBlob: Blob) {
    const formData = new FormData();
    formData.append('path', 'upload-file');
    formData.append('purpose', 'cvReview');
    formData.append('file', pdfBlob, 'My_CV.pdf');

    try {
      const res: any = await this.io._uploadFile(formData).toPromise();

      const payLoad = {
        path: '/cv-review',
        file: [{ Key: res['result'].id + '_' + res['result'].fileName }],
      };

      const feedbackRes: any = await this.io
        ._getCvFeedback(payLoad)
        .toPromise();

      if (feedbackRes.serverResponse.code === 200) {
        this.geminiSuggestions = feedbackRes.result.points;
        this.geminiRefinedCV = feedbackRes.result.resumedata;
      } else {
        throw new Error(
          feedbackRes.body || 'Server responded with an error during CV review.'
        );
      }
    } catch (err) {
      throw err;
    }
  }
}
